import { Tooltip } from '@/components/common/tooltip';
import { palette } from '@/lib/design-tokens';

import { formatCoordinates, formatLatLon } from '../format';
import type { SelectedDevice } from '../types';

import styles from './requested-devices-cell.module.scss';

/** `labeled` = the "Lat: X | Lon: Y" tooltip format (Figma 30:398967); the inline cell keeps
 *  the compact "X, Y" to fit the narrow column. */
function deviceLine(d: SelectedDevice, key: string, labeled = false) {
  return (
    <span key={key} className={styles.device}>
      <span className={styles.name}>{d.deviceName}</span>
      <span className={styles.coords}>
        {labeled
          ? formatLatLon(d.latitude, d.longitude)
          : formatCoordinates(d.latitude, d.longitude)}
      </span>
    </span>
  );
}

/** Selected Device cell: first requested device + coordinates; when >1, a dark `N+` badge and a
 *  hover tooltip (light card, Figma 30:398962) listing every requested device with its labeled
 *  coordinates (ADR-0018).
 *
 *  **The whole cell is the hover target, not just the badge** (ADO 119718 / 119717 #1). This is
 *  the last column of a table that is wider than its card, so the badge — pinned to the cell's
 *  right edge — spends most of the horizontal scroll range either off-screen or underneath the
 *  sticky right-hand action column, which swallows the pointer: measured with
 *  `document.elementFromPoint`, the badge answers as `td.ant-table-cell-fix-end` at every scroll
 *  offset but the last. The device name sits at the cell's left edge and comes into reach long
 *  before the badge does, so triggering on the cell makes "hover over the selected device" work
 *  wherever the table is scrolled. The badge stays inside the trigger, so AC 109527's "hovers
 *  over the count chip" still holds. */
export function RequestedDevicesCell({ devices }: { devices: SelectedDevice[] }) {
  if (devices.length === 0) return <span>-</span>;

  const [first, ...rest] = devices;
  if (rest.length === 0) return deviceLine(first, 'first');

  const all = (
    <span className={styles.list}>
      {devices.map((d, i) => deviceLine(d, `${d.deviceName}-${i}`, true))}
    </span>
  );

  return (
    <Tooltip appearance="light" color={palette.surface.card} placement="bottom" title={all}>
      <button
        type="button"
        className={styles.cell}
        aria-label={`${devices.length} requested devices`}
      >
        {deviceLine(first, 'first')}
        <span className={styles.badge} aria-hidden>
          {rest.length}+
        </span>
      </button>
    </Tooltip>
  );
}
